import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { DEFAULT_MARKET_TYPE_TAB_ORDER, orderMarketTypes } from '@cou/contracts';
import {
  Banner,
  Button,
  Card,
  Checkbox,
  Collapsible,
  CountBadge,
  Crest,
  CrestPair,
  Input,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  MarketRow,
  SearchInput,
  SegmentedControl,
  Select,
  SetupRail,
  StatusPill,
  IdChip,
  useCollapsibleSections,
  VerdictBadge,
  toast,
  type SetupRailStep,
} from '@cou/admin-ui';
import { toDisplayVerdict } from '../catalogue/catalogue-client';
import { hasQuote } from '../catalogue/format';
import { crestsForEventName, lookupTeam } from '../catalogue/teams';
import {
  defaultContestsClient,
  type AdminContestWithEntryCount,
  type ContestsClient,
  type UpdateContestPatch,
} from '../contests/contests-client';
import {
  defaultEditorCatalogueClient,
  type CatalogueEvent,
  type CatalogueMarket,
  type EditorCatalogueClient,
} from '../contests/editor-catalogue-client';
import { defaultConfigClient, type ConfigClient } from '../config/config-client';
import { dateTimeLocalToIso, formatRailDate } from '../contests/format-date';
import { payoutMatrixSegments, type PayoutMatrixDraft } from '../prizes/payout-matrix';
import type { RewardCell } from '../prizes/reward-cell';
import { PayoutMatrixEditor, PrizeCellModal } from '../prizes/PayoutMatrixEditor';
import { contestPillState } from '../contests/state-pill';
import { useAdminAuth } from '../session/admin-auth-context';
import { AdminShell } from '../shell/AdminShell';

export interface ContestEditorScreenProps {
  /** Injected for tests; defaults to the real API-09 client. */
  readonly contestsClient?: ContestsClient;
  /** Injected for tests; defaults to the real API-16 catalogue client. */
  readonly catalogueClient?: EditorCatalogueClient;
  /** Injected for tests; defaults to the real API-10 config client (segment registry). */
  readonly configClient?: ConfigClient;
}

type SetupStepKey = 'basic-info' | 'slate' | 'prizes' | 'rules';

function parseSetupStep(value: string | null): SetupStepKey {
  if (value === 'basic-info' || value === 'slate' || value === 'prizes' || value === 'rules') {
    return value;
  }
  return 'basic-info';
}

interface EventDetail {
  readonly name: string;
  readonly startTime?: string;
  readonly competition?: string | null;
}

const PlayerVisibleHint = (
  <span className="inline-flex items-center gap-1.5 text-accent">
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z"
        stroke="currentColor"
        strokeWidth="1.6"
      />
      <circle cx="12" cy="12" r="2.6" stroke="currentColor" strokeWidth="1.6" />
    </svg>
    Player-visible — served to the game in the public contest feed
  </span>
);

/** The Basic-info-editable subset of AdminContest, as `<input>`-friendly strings. */
interface BasicInfoDraft {
  name: string;
  campaignId: string;
  round: string;
  opensAt: string;
  closesAt: string;
}

/** The curated Slate — array order is display order (database.md); both halves travel together. */
interface CurationDraft {
  eventIds: string[];
  marketIds: string[];
}

/** The Contest's snapshotted configuration (INV-08), editable as per-Contest overrides
 * (admin-ui.md Rules: pick min/max, void policy, correction window). */
interface RulesDraft {
  [key: string]: unknown;
  minPicks: number;
  maxPicks: number;
  lockPolicy: string;
  voidPolicy: string;
  pushPolicy: string;
  resultCorrectionWindowMinutes: number;
}

function isoToDateTimeLocal(iso: string | null): string {
  if (!iso) return '';
  return iso.slice(0, 16);
}

function matrixFromContest(contest: AdminContestWithEntryCount): PayoutMatrixDraft {
  return JSON.parse(JSON.stringify(contest.payoutMatrix)) as PayoutMatrixDraft;
}

function rulesFromContest(contest: AdminContestWithEntryCount): RulesDraft {
  const config = contest.config as Partial<RulesDraft>;
  return {
    minPicks: config.minPicks ?? 6,
    maxPicks: config.maxPicks ?? 6,
    lockPolicy: config.lockPolicy ?? 'per-entry-earliest-kickoff',
    voidPolicy: config.voidPolicy ?? 'loss',
    pushPolicy: config.pushPolicy ?? 'loss',
    resultCorrectionWindowMinutes: config.resultCorrectionWindowMinutes ?? 120,
  };
}

function basicInfoFromContest(contest: AdminContestWithEntryCount): BasicInfoDraft {
  return {
    name: contest.name,
    campaignId: contest.campaignId,
    round: contest.round,
    opensAt: isoToDateTimeLocal(contest.opensAt),
    closesAt: isoToDateTimeLocal(contest.closesAt),
  };
}

function curationFromContest(contest: AdminContestWithEntryCount): CurationDraft {
  return {
    eventIds: [...(contest.curation?.eventIds ?? [])],
    marketIds: [...(contest.curation?.marketIds ?? [])],
  };
}

function sameArray(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}

/** The server accepts only a fully-empty curation (an explicit un-curate) or a fully-populated
 * one — a one-sided body (Events staged, no Markets yet) is rejected 422 `curation_empty`
 * (CAE-308/CAE-336). The editor lets an operator stage Events before curating Markets (admin-ui.md),
 * so that in-between state is real and must not be sent — or it fails the whole save. */
function curationIsSaveable(curation: CurationDraft): boolean {
  const bothEmpty = curation.eventIds.length === 0 && curation.marketIds.length === 0;
  const bothPopulated = curation.eventIds.length > 0 && curation.marketIds.length > 0;
  return bothEmpty || bothPopulated;
}

/** The changed fields between the saved baseline and the current draft, as a PUT patch.
 * Curation halves always travel together when either changes (CAE-308/CAE-336); a one-sided
 * draft is left out of the patch entirely so it can't fail an otherwise-valid save. */
function buildPatch(
  savedInfo: BasicInfoDraft,
  draftInfo: BasicInfoDraft,
  savedCuration: CurationDraft,
  draftCuration: CurationDraft,
  savedMatrix: PayoutMatrixDraft,
  draftMatrix: PayoutMatrixDraft,
  savedRules: RulesDraft,
  draftRules: RulesDraft,
): UpdateContestPatch {
  const patch: UpdateContestPatch = {};
  if (draftInfo.name !== savedInfo.name) patch.name = draftInfo.name;
  if (draftInfo.campaignId !== savedInfo.campaignId) patch.campaignId = draftInfo.campaignId;
  if (draftInfo.round !== savedInfo.round) patch.round = draftInfo.round;
  if (draftInfo.opensAt !== savedInfo.opensAt && draftInfo.opensAt !== '') {
    patch.opensAt = dateTimeLocalToIso(draftInfo.opensAt);
  }
  if (draftInfo.closesAt !== savedInfo.closesAt) {
    patch.closesAt = draftInfo.closesAt === '' ? null : dateTimeLocalToIso(draftInfo.closesAt);
  }
  const curationChanged =
    !sameArray(savedCuration.eventIds, draftCuration.eventIds) ||
    !sameArray(savedCuration.marketIds, draftCuration.marketIds);
  if (curationChanged && curationIsSaveable(draftCuration)) {
    patch.eventIds = draftCuration.eventIds;
    patch.marketIds = draftCuration.marketIds;
  }
  if (JSON.stringify(savedMatrix) !== JSON.stringify(draftMatrix)) {
    patch.payoutMatrix = draftMatrix;
  }
  if (JSON.stringify(savedRules) !== JSON.stringify(draftRules)) {
    patch.config = draftRules;
  }
  return patch;
}

export function ContestEditorScreen({
  contestsClient = defaultContestsClient,
  catalogueClient = defaultEditorCatalogueClient,
  configClient = defaultConfigClient,
}: ContestEditorScreenProps) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { claims, getToken } = useAdminAuth();
  const canManage = claims?.permissions.includes('contests:manage') ?? false;
  const [step, setStep] = useState<SetupStepKey>(() => parseSetupStep(searchParams.get('step')));
  const [contest, setContest] = useState<AdminContestWithEntryCount | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [savedInfo, setSavedInfo] = useState<BasicInfoDraft | null>(null);
  const [draftInfo, setDraftInfo] = useState<BasicInfoDraft | null>(null);
  const [savedCuration, setSavedCuration] = useState<CurationDraft | null>(null);
  const [draftCuration, setDraftCuration] = useState<CurationDraft | null>(null);
  const [savedMatrix, setSavedMatrix] = useState<PayoutMatrixDraft | null>(null);
  const [draftMatrix, setDraftMatrix] = useState<PayoutMatrixDraft | null>(null);
  const [savedRules, setSavedRules] = useState<RulesDraft | null>(null);
  const [draftRules, setDraftRules] = useState<RulesDraft | null>(null);
  const [segmentRegistry, setSegmentRegistry] = useState<Record<string, string>>({});
  const [marketTypeTabOrder, setMarketTypeTabOrder] = useState<readonly string[]>([
    ...DEFAULT_MARKET_TYPE_TAB_ORDER,
  ]);
  const [rulesDefaults, setRulesDefaults] = useState<RulesDefaults | null>(null);
  const [cellEditor, setCellEditor] = useState<{ segment: string; tier: string } | null>(null);
  // CatalogueEvent (Add-events) and CuratedEvent (already-curated load) both satisfy this.
  const [eventDetails, setEventDetails] = useState<Record<string, EventDetail>>({});
  const [marketsByEvent, setMarketsByEvent] = useState<Record<string, readonly CatalogueMarket[]>>(
    {},
  );
  const [grouping, setGrouping] = useState<'event' | 'market-type'>('event');
  const [slateSearch, setSlateSearch] = useState('');
  const [addEventsOpen, setAddEventsOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const token = getToken();
    if (token === null || id === undefined) return;
    let cancelled = false;
    void (async () => {
      const result = await contestsClient.getContest(token, id);
      if (cancelled) return;
      if (!result.ok) {
        setLoadError(`Could not load this contest (${result.code ?? result.status}).`);
        return;
      }
      const info = basicInfoFromContest(result.contest);
      const curation = curationFromContest(result.contest);
      const matrix = matrixFromContest(result.contest);
      const rules = rulesFromContest(result.contest);
      setContest(result.contest);
      setSavedInfo(info);
      setDraftInfo(info);
      setSavedCuration(curation);
      setDraftCuration(curation);
      setSavedMatrix(matrix);
      setDraftMatrix(matrix);
      setSavedRules(rules);
      setDraftRules(rules);
      setLoadError(null);

      // Resolve display detail for the already-curated Events, mirroring what the Add-events
      // flow fetches for a newly-added one — the curation document carries only pointers.
      void catalogueClient.getCuratedEvents(token, curation.eventIds).then((events) => {
        if (cancelled) return;
        setEventDetails((prev) => {
          const next = { ...prev };
          for (const event of events) next[event.id] = event;
          return next;
        });
      });
      await Promise.all(
        curation.eventIds.map(async (eventId) => {
          const marketsResult = await catalogueClient.listMarkets(token, eventId);
          if (cancelled || !marketsResult.ok) return;
          setMarketsByEvent((prev) => ({ ...prev, [eventId]: marketsResult.markets }));
        }),
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [contestsClient, catalogueClient, getToken, id]);

  // The segment registry (API-10) doesn't depend on the contest — same source and shape as the
  // detail page's read-only Prizes tab, so the editor's columns always match what it'll show.
  useEffect(() => {
    const token = getToken();
    if (token === null) return;
    let cancelled = false;
    void (async () => {
      const result = await configClient.getConfig(token);
      if (cancelled || !result.ok) return;
      const registry = result.config['segment_registry'];
      if (registry && typeof registry === 'object') {
        setSegmentRegistry(registry as Record<string, string>);
      }
      const tabOrder = result.config['market_type_tab_order'];
      if (Array.isArray(tabOrder) && tabOrder.every((entry) => typeof entry === 'string')) {
        setMarketTypeTabOrder(tabOrder);
      }
      const defaults = result.config['default_configuration'];
      if (defaults && typeof defaults === 'object') {
        const cfg = defaults as Partial<RulesDefaults>;
        setRulesDefaults({
          minPicks: cfg.minPicks ?? 6,
          maxPicks: cfg.maxPicks ?? 6,
          voidPolicy: cfg.voidPolicy ?? 'loss',
          resultCorrectionWindowMinutes: cfg.resultCorrectionWindowMinutes ?? 120,
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [configClient, getToken]);

  const dirty = useMemo(() => {
    if (
      !savedInfo ||
      !draftInfo ||
      !savedCuration ||
      !draftCuration ||
      !savedMatrix ||
      !draftMatrix ||
      !savedRules ||
      !draftRules
    ) {
      return false;
    }
    return (
      Object.keys(
        buildPatch(
          savedInfo,
          draftInfo,
          savedCuration,
          draftCuration,
          savedMatrix,
          draftMatrix,
          savedRules,
          draftRules,
        ),
      ).length > 0
    );
  }, [
    savedInfo,
    draftInfo,
    savedCuration,
    draftCuration,
    savedMatrix,
    draftMatrix,
    savedRules,
    draftRules,
  ]);

  async function saveChanges() {
    const token = getToken();
    if (
      token === null ||
      id === undefined ||
      savedInfo === null ||
      draftInfo === null ||
      savedCuration === null ||
      draftCuration === null ||
      savedMatrix === null ||
      draftMatrix === null ||
      savedRules === null ||
      draftRules === null
    ) {
      return;
    }
    const patch = buildPatch(
      savedInfo,
      draftInfo,
      savedCuration,
      draftCuration,
      savedMatrix,
      draftMatrix,
      savedRules,
      draftRules,
    );
    if (Object.keys(patch).length === 0) return;
    setSaving(true);
    const result = await contestsClient.updateContest(token, id, patch);
    setSaving(false);
    if (result.ok) {
      const { ok: _ok, ...updated } = result;
      setContest((prev) => (prev ? { ...prev, ...updated } : prev));
      setSavedInfo(draftInfo);
      // Only mark curation "saved" when it actually rode this patch — a one-sided draft
      // (Events staged, no Markets yet) is deliberately excluded from the request (see
      // buildPatch), so it must stay "dirty" rather than being marked persisted here.
      if (patch.eventIds !== undefined) setSavedCuration(draftCuration);
      setSavedMatrix(draftMatrix);
      setSavedRules(draftRules);
      toast.success('Changes saved');
    } else {
      toast.error(result.message ?? `Could not save changes (${result.code ?? result.status}).`);
    }
  }

  function setCellReward(segment: string, tier: string, cell: RewardCell | null) {
    if (draftMatrix === null) return;
    const nextSegment = { ...draftMatrix[segment] };
    if (cell === null) {
      delete nextSegment[tier];
    } else {
      nextSegment[tier] = cell;
    }
    setDraftMatrix({ ...draftMatrix, [segment]: nextSegment });
    setCellEditor(null);
  }

  function addEvents(events: readonly CatalogueEvent[]) {
    const token = getToken();
    if (token === null || draftCuration === null) return;
    setEventDetails((prev) => {
      const next = { ...prev };
      for (const event of events) next[event.id] = event;
      return next;
    });
    setDraftCuration({
      ...draftCuration,
      eventIds: [...draftCuration.eventIds, ...events.map((event) => event.id)],
    });
    for (const event of events) {
      void catalogueClient.listMarkets(token, event.id).then((result) => {
        if (!result.ok) return;
        setMarketsByEvent((prev) => ({ ...prev, [event.id]: result.markets }));
      });
    }
    setAddEventsOpen(false);
  }

  function toggleMarket(marketId: string, checked: boolean) {
    if (draftCuration === null) return;
    setDraftCuration({
      ...draftCuration,
      marketIds: checked
        ? [...draftCuration.marketIds, marketId]
        : draftCuration.marketIds.filter((id) => id !== marketId),
    });
  }

  /**
   * Bulk select/deselect (the Select-all controls, GL-14). Selecting appends the given ids
   * that aren't already curated, in their passed display order, so contest_markets.display_order
   * stays the array order; deselecting removes them. A no-op for ids already in the wanted state.
   */
  function setMarketsSelected(marketIds: readonly string[], selected: boolean) {
    if (draftCuration === null) return;
    if (selected) {
      const present = new Set(draftCuration.marketIds);
      const additions = marketIds.filter((id) => !present.has(id));
      if (additions.length === 0) return;
      setDraftCuration({
        ...draftCuration,
        marketIds: [...draftCuration.marketIds, ...additions],
      });
    } else {
      const removing = new Set(marketIds);
      setDraftCuration({
        ...draftCuration,
        marketIds: draftCuration.marketIds.filter((id) => !removing.has(id)),
      });
    }
  }

  function removeEvent(eventId: string) {
    if (draftCuration === null) return;
    const eventMarketIds = new Set((marketsByEvent[eventId] ?? []).map((market) => market.id));
    setDraftCuration({
      eventIds: draftCuration.eventIds.filter((id) => id !== eventId),
      marketIds: draftCuration.marketIds.filter((id) => !eventMarketIds.has(id)),
    });
  }

  function discardChanges() {
    if (savedInfo) setDraftInfo({ ...savedInfo });
    if (savedCuration) setDraftCuration({ ...savedCuration });
    if (savedMatrix) setDraftMatrix(JSON.parse(JSON.stringify(savedMatrix)) as PayoutMatrixDraft);
    if (savedRules) setDraftRules({ ...savedRules });
  }

  if (!canManage && id !== undefined) {
    return <Navigate to={`/contests/${id}`} replace />;
  }

  if (loadError) {
    return (
      <AdminShell title="Edit contest">
        <Banner variant="danger">{loadError}</Banner>
      </AdminShell>
    );
  }

  if (
    contest === null ||
    draftInfo === null ||
    draftCuration === null ||
    draftMatrix === null ||
    draftRules === null
  ) {
    return <AdminShell title="Edit contest">Loading…</AdminShell>;
  }

  const requiredBasicInfoMissing =
    draftInfo.name === '' ||
    draftInfo.campaignId === '' ||
    draftInfo.round === '' ||
    draftInfo.opensAt === '';

  const pill = contestPillState(contest.state, contest.finalisedAt);
  const railSteps = buildSetupRailSteps({
    basicDone: !requiredBasicInfoMissing,
    eventCount: draftCuration.eventIds.length,
    propCount: draftCuration.marketIds.length,
    matrix: draftMatrix,
    segmentRegistry,
    maxPicks: draftRules.maxPicks,
  });

  return (
    <AdminShell
      title="Edit contest"
      subtitle="Configure the slate, prizes, and rules for this contest."
      meta={
        <>
          <StatusPill state={pill} />
          <IdChip label="ID" value={contest.id.slice(0, 8)} title={contest.id} />
        </>
      }
      actions={
        <div className="flex items-center gap-2.5">
          <Button variant="ghost" onClick={() => void navigate(`/contests/${contest.id}`)}>
            Back to overview
          </Button>
          <Button variant="outline" disabled={!dirty || saving} onClick={discardChanges}>
            Discard
          </Button>
          <Button
            disabled={!dirty || saving || requiredBasicInfoMissing}
            onClick={() => void saveChanges()}
          >
            Save changes
          </Button>
        </div>
      }
    >
      <SetupRail
        steps={railSteps}
        current={step}
        onNavigate={(key) => setStep(key as SetupStepKey)}
      />
      <div className="mt-6">
        {step === 'basic-info' && (
          <Card title="Basic information">
            <div className="grid grid-cols-2 gap-x-5 gap-y-4">
              <Input
                className="col-span-2"
                label="Contest name"
                required
                hint={PlayerVisibleHint}
                value={draftInfo.name}
                onChange={(event) => setDraftInfo({ ...draftInfo, name: event.target.value })}
              />
              <Input
                label="Campaign ID"
                required
                mono
                hint="Required before finalise"
                value={draftInfo.campaignId}
                onChange={(event) => setDraftInfo({ ...draftInfo, campaignId: event.target.value })}
              />
              <Input
                label="Round"
                required
                mono
                value={draftInfo.round}
                onChange={(event) => setDraftInfo({ ...draftInfo, round: event.target.value })}
              />
              <Input
                label="Opens at"
                type="datetime-local"
                required
                mono
                hint="Scheduler opens the contest at this time"
                value={draftInfo.opensAt}
                onChange={(event) => setDraftInfo({ ...draftInfo, opensAt: event.target.value })}
              />
              <Input
                label="Closes at (override)"
                type="datetime-local"
                mono
                hint="Defaults to the last event start"
                value={draftInfo.closesAt}
                onChange={(event) => setDraftInfo({ ...draftInfo, closesAt: event.target.value })}
              />
            </div>
          </Card>
        )}
        {step === 'slate' && (
          <Card
            title={
              <>
                Slate{' '}
                <span className="font-ui text-[12.5px] font-normal text-ink-3">
                  — <span className="font-mono">{draftCuration.eventIds.length}</span> events ·{' '}
                  <span className="font-mono">{draftCuration.marketIds.length}</span> props
                </span>
              </>
            }
            action={
              <div className="flex items-center gap-2">
                <SearchInput
                  className="w-[240px]"
                  placeholder="Search events or props"
                  value={slateSearch}
                  onChange={(event) => setSlateSearch(event.target.value)}
                />
                <Button onClick={() => setAddEventsOpen(true)}>+ Add events</Button>
              </div>
            }
          >
            <div className="mb-1">
              <SegmentedControl
                size="sm"
                options={[
                  { value: 'event', label: 'By event' },
                  { value: 'market-type', label: 'By market type' },
                ]}
                value={grouping}
                onChange={(value) => setGrouping(value as 'event' | 'market-type')}
              />
            </div>
            {draftCuration.eventIds.length > 0 && draftCuration.marketIds.length === 0 && (
              <Banner variant="warn" title="Not saved yet">
                Events are added, but no props are curated. Check at least one prop below before
                Save changes — a Slate with Events and no props can't be saved, and this list is
                lost on refresh until then.
              </Banner>
            )}
            {draftCuration.eventIds.length === 0 ? (
              <p className="font-ui text-sm text-ink-3">
                No Events added yet. Add Events from the catalogue to start curating props.
              </p>
            ) : grouping === 'event' ? (
              <SlateByEvent
                eventIds={draftCuration.eventIds}
                marketIds={draftCuration.marketIds}
                eventDetails={eventDetails}
                marketsByEvent={marketsByEvent}
                search={slateSearch}
                onToggleMarket={toggleMarket}
                onSetMarketsSelected={setMarketsSelected}
                onRemoveEvent={removeEvent}
              />
            ) : (
              <SlateByMarketType
                marketIds={draftCuration.marketIds}
                eventDetails={eventDetails}
                marketsByEvent={marketsByEvent}
                search={slateSearch}
                marketTypeTabOrder={marketTypeTabOrder}
              />
            )}
            <p className="m-0 font-ui text-[12.5px] text-ink-4">
              Lines and outcomes freeze at finalisation. Until then, verdicts and quotes update live
              from the feed.
            </p>
          </Card>
        )}
        {step === 'prizes' && (
          <>
            {/* Where this matrix came from (INV-08): copied from the defaults at creation, edited
                here per Contest. Without it an operator reads the grid as the global defaults. */}
            <Banner variant="info" title="Inherited from the default prize tiers">
              Copied from Prize Tiers when this contest was created. Edits here stay on this contest
              and never change the defaults.
            </Banner>
            <PayoutMatrixEditor
              matrix={draftMatrix}
              segmentRegistry={segmentRegistry}
              maxPicks={draftRules.maxPicks}
              eyebrow="Payout matrix"
              title="Correct picks × player segments"
              emptyLabel="+ Add tier"
              hint="Each paying cell delivers one reward. Unset segments block finalise until a reward is set or the segment is removed."
              onEditCell={(segment, tier) => setCellEditor({ segment, tier })}
            />
          </>
        )}
        {step === 'rules' && (
          <RulesEditor rules={draftRules} inherited={rulesDefaults} onChange={setDraftRules} />
        )}
      </div>
      {addEventsOpen && (
        <AddEventsModal
          contestId={contest.id}
          catalogueClient={catalogueClient}
          alreadyAdded={new Set(draftCuration.eventIds)}
          onClose={() => setAddEventsOpen(false)}
          onAdd={addEvents}
        />
      )}
      {cellEditor && (
        <PrizeCellModal
          segmentLabel={
            cellEditor.segment === 'default'
              ? 'Default'
              : (segmentRegistry[cellEditor.segment] ?? cellEditor.segment)
          }
          tier={cellEditor.tier}
          cell={draftMatrix[cellEditor.segment]?.[cellEditor.tier] ?? null}
          onClose={() => setCellEditor(null)}
          onSave={(cell) => setCellReward(cellEditor.segment, cellEditor.tier, cell)}
        />
      )}
    </AdminShell>
  );
}

interface SlateByEventProps {
  readonly eventIds: readonly string[];
  readonly marketIds: readonly string[];
  readonly eventDetails: Record<string, EventDetail>;
  readonly marketsByEvent: Record<string, readonly CatalogueMarket[]>;
  readonly search: string;
  readonly onToggleMarket: (marketId: string, checked: boolean) => void;
  readonly onSetMarketsSelected: (marketIds: readonly string[], selected: boolean) => void;
  readonly onRemoveEvent: (eventId: string) => void;
}

/** Which props the CAE-486 selection filter shows: everything, only curated, or only uncurated. */
type SlateSelectionFilter = 'all' | 'selected' | 'unselected';

/** Tri-state for a Select-all control: none of its props curated, some, or all (GL-14). */
function selectAllState(selected: number, total: number): boolean | 'indeterminate' {
  if (total > 0 && selected === total) return true;
  if (selected > 0) return 'indeterminate';
  return false;
}

/** A Select-all checkbox that lives inside a clickable accordion header — its own clicks/keys
 * must not bubble up and toggle the section's collapse. */
function SelectAllCheckbox({
  label,
  selected,
  total,
  onToggle,
}: {
  label: string;
  selected: number;
  total: number;
  onToggle: (select: boolean) => void;
}) {
  return (
    <span
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      <Checkbox
        aria-label={label}
        checked={selectAllState(selected, total)}
        onCheckedChange={() => onToggle(selected !== total)}
        label={<span className="text-ink-3">Select all</span>}
      />
    </span>
  );
}

/** The curation view: a filter/select-all/expand toolbar, then one collapsible block per added
 * Event, each split into collapsible market-type groups of selectable prop rows (CAE-486). Array
 * order of selected market ids becomes contest_markets.display_order. */
function SlateByEvent({
  eventIds,
  marketIds,
  eventDetails,
  marketsByEvent,
  search,
  onToggleMarket,
  onSetMarketsSelected,
  onRemoveEvent,
}: SlateByEventProps) {
  const [filter, setFilter] = useState<SlateSelectionFilter>('all');
  const eventSections = useCollapsibleSections();
  const groupSections = useCollapsibleSections();

  const query = search.trim().toLowerCase();
  const curatedIds = useMemo(() => new Set(marketIds), [marketIds]);
  const passesFilter = (curated: boolean) =>
    filter === 'all' || (filter === 'selected' ? curated : !curated);

  // Every prop across the slate, in event-then-market order — the scope the global Select-all acts on.
  const allMarketIds = eventIds.flatMap((eventId) =>
    (marketsByEvent[eventId] ?? []).map((market) => market.id),
  );
  const curatedTotal = allMarketIds.filter((mktId) => curatedIds.has(mktId)).length;

  function collapseAll() {
    const groupKeys: string[] = [];
    for (const eventId of eventIds) {
      const seen = new Set<string>();
      for (const market of marketsByEvent[eventId] ?? []) {
        if (!seen.has(market.marketType)) {
          seen.add(market.marketType);
          groupKeys.push(`${eventId}::${market.marketType}`);
        }
      }
    }
    eventSections.collapse(eventIds);
    groupSections.collapse(groupKeys);
  }

  function expandAll() {
    eventSections.expandAll();
    groupSections.expandAll();
  }

  const eventBlocks = eventIds.map((eventId) => {
    const event = eventDetails[eventId];
    const name = event?.name ?? `Event ${eventId.slice(0, 8)}`;
    const marketsLoaded = eventId in marketsByEvent;
    const markets = marketsByEvent[eventId] ?? [];
    if (
      query &&
      !name.toLowerCase().includes(query) &&
      !markets.some((market) => market.name.toLowerCase().includes(query))
    ) {
      return null;
    }
    const crests = crestsForEventName(name);
    const competition = event?.competition?.trim() || 'MLB';
    const meta = event?.startTime
      ? `${competition} · ${formatRailDate(event.startTime)}`
      : competition;

    const byType = new Map<string, CatalogueMarket[]>();
    for (const market of markets) {
      if (
        query &&
        !market.name.toLowerCase().includes(query) &&
        !name.toLowerCase().includes(query)
      ) {
        continue;
      }
      const list = byType.get(market.marketType) ?? [];
      list.push(market);
      byType.set(market.marketType, list);
    }

    const eventMarketIds = markets.map((market) => market.id);
    const eventCurated = eventMarketIds.filter((mktId) => curatedIds.has(mktId)).length;

    // Groups keep every prop for its select-all/count, but only render those passing the filter.
    const groups = Array.from(byType.entries())
      .map(([type, typed]) => {
        const groupMarketIds = typed.map((market) => market.id);
        const groupCurated = groupMarketIds.filter((mktId) => curatedIds.has(mktId)).length;
        const visible = typed.filter((market) => passesFilter(curatedIds.has(market.id)));
        return { type, typed, groupMarketIds, groupCurated, visible };
      })
      .filter((group) => group.visible.length > 0);

    // Under a selection filter, an Event with no matching props drops out entirely.
    if (filter !== 'all' && groups.length === 0) return null;

    const eventCollapsed = eventSections.isCollapsed(eventId);

    return (
      <div
        key={eventId}
        className="flex flex-col gap-3 border-t-[0.5px] border-line pt-4 first:border-t-0 first:pt-0"
      >
        <Collapsible
          expanded={!eventCollapsed}
          onToggle={() => eventSections.toggle(eventId)}
          label={`Toggle ${name} props`}
          headerClassName="gap-3"
          header={
            <>
              {crests ? (
                <CrestPair home={crests.home} away={crests.away} />
              ) : (
                <Crest {...lookupTeam(name)} />
              )}
              <span className="whitespace-nowrap font-ui text-[14.5px] font-semibold leading-[1.3] tracking-[-0.005em] text-ink">
                {name}
              </span>
              <span className="whitespace-nowrap font-mono text-[12.5px] text-ink-4">{meta}</span>
              <span className="inline-flex items-center gap-1.5 whitespace-nowrap font-ui text-[12.5px] text-ink-3">
                <CountBadge count={eventCurated} /> of {eventMarketIds.length} props
              </span>
              {eventMarketIds.length > 0 && (
                <span className="ml-auto">
                  <SelectAllCheckbox
                    label={`Select all props for ${name}`}
                    selected={eventCurated}
                    total={eventMarketIds.length}
                    onToggle={(select) => onSetMarketsSelected(eventMarketIds, select)}
                  />
                </span>
              )}
            </>
          }
        >
          {groups.map(({ type, groupMarketIds, groupCurated, visible }) => {
            const groupKey = `${eventId}::${type}`;
            const groupCollapsed = groupSections.isCollapsed(groupKey);
            return (
              <div key={type} className="flex flex-col gap-2">
                <Collapsible
                  expanded={!groupCollapsed}
                  onToggle={() => groupSections.toggle(groupKey)}
                  label={`Toggle ${type} props for ${name}`}
                  header={
                    <>
                      <span className="font-ui text-[11px] font-medium uppercase tracking-[0.08em] text-ink-3">
                        {type}
                      </span>
                      <CountBadge count={groupCurated} />
                      <span className="ml-auto">
                        <SelectAllCheckbox
                          label={`Select all ${type} props for ${name}`}
                          selected={groupCurated}
                          total={groupMarketIds.length}
                          onToggle={(select) => onSetMarketsSelected(groupMarketIds, select)}
                        />
                      </span>
                    </>
                  }
                >
                  <div className="flex flex-col gap-2">
                    {visible.map((market) => {
                      const curated = curatedIds.has(market.id);
                      return (
                        <MarketRow
                          key={market.id}
                          type={market.marketType}
                          name={market.name}
                          line={market.line.toFixed(1)}
                          quote={hasQuote(market.quote) ? market.quote : undefined}
                          verdict={<VerdictBadge verdict={toDisplayVerdict(market.verdict)} />}
                          muted={market.verdict === 'suspended'}
                          selected={curated}
                          onToggleSelect={() => onToggleMarket(market.id, !curated)}
                          select={
                            <Checkbox
                              aria-label={`Include ${market.name} in the slate`}
                              checked={curated}
                              onCheckedChange={(checked) =>
                                onToggleMarket(market.id, checked === true)
                              }
                            />
                          }
                        />
                      );
                    })}
                  </div>
                </Collapsible>
              </div>
            );
          })}
          <div className="pt-1">
            <button
              type="button"
              disabled={!marketsLoaded}
              className="rounded-md px-0 py-1 font-ui text-[13px] font-medium text-accent hover:underline disabled:cursor-not-allowed disabled:opacity-45 disabled:no-underline"
              onClick={() => onRemoveEvent(eventId)}
            >
              Remove event →
            </button>
          </div>
        </Collapsible>
      </div>
    );
  });

  const anyVisible = eventBlocks.some((block) => block !== null);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3 border-b-[0.5px] border-line pb-3">
        <span className="font-ui text-[12.5px] text-ink-3">Show</span>
        <SegmentedControl
          size="sm"
          options={[
            { value: 'all', label: 'All' },
            { value: 'selected', label: 'Selected' },
            { value: 'unselected', label: 'Unselected' },
          ]}
          value={filter}
          onChange={(value) => setFilter(value as SlateSelectionFilter)}
        />
        <div className="ml-auto flex items-center gap-2">
          {allMarketIds.length > 0 && (
            <Checkbox
              aria-label="Select all props in the slate"
              checked={selectAllState(curatedTotal, allMarketIds.length)}
              onCheckedChange={() =>
                onSetMarketsSelected(allMarketIds, curatedTotal !== allMarketIds.length)
              }
              label={<span className="text-ink-3">Select all</span>}
            />
          )}
          <Button variant="ghost" size="sm" onClick={expandAll}>
            Expand all
          </Button>
          <Button variant="ghost" size="sm" onClick={collapseAll}>
            Collapse all
          </Button>
        </div>
      </div>
      {anyVisible ? (
        eventBlocks
      ) : (
        <p className="font-ui text-sm text-ink-3">
          {filter === 'selected'
            ? 'No props are selected yet. Switch to All to start curating.'
            : filter === 'unselected'
              ? 'Every prop is already selected. Switch to All to review the slate.'
              : 'No events or props match your search.'}
        </p>
      )}
    </div>
  );
}

interface SlateByMarketTypeProps {
  readonly marketIds: readonly string[];
  readonly eventDetails: Record<string, EventDetail>;
  readonly marketsByEvent: Record<string, readonly CatalogueMarket[]>;
  readonly search: string;
  /** Public `market_type_tab_order` — same ranking as the Play tab (CAE-523). */
  readonly marketTypeTabOrder: readonly string[];
}

/** The player-facing pivot: every curated prop of one market_type together, in tab order —
 * a client-side read over the same curated set (database.md), no separate curation controls. */
function SlateByMarketType({
  marketIds,
  eventDetails,
  marketsByEvent,
  search,
  marketTypeTabOrder,
}: SlateByMarketTypeProps) {
  const query = search.trim().toLowerCase();
  const allMarkets = Object.entries(marketsByEvent).flatMap(([eventId, markets]) =>
    markets.map((market) => ({ ...market, eventId })),
  );
  const curated = marketIds
    .map((id) => allMarkets.find((market) => market.id === id))
    .filter((market): market is (typeof allMarkets)[number] => market !== undefined)
    .filter(
      (market) =>
        !query ||
        market.name.toLowerCase().includes(query) ||
        (eventDetails[market.eventId]?.name ?? '').toLowerCase().includes(query),
    );

  const groups = new Map<string, typeof curated>();
  for (const market of curated) {
    const list = groups.get(market.marketType) ?? [];
    list.push(market);
    groups.set(market.marketType, list);
  }

  const orderedTypes = orderMarketTypes([...groups.keys()], marketTypeTabOrder);

  return (
    <div className="flex flex-col gap-4">
      {orderedTypes.map((marketType) => {
        const markets = groups.get(marketType) ?? [];
        return (
          <div
            key={marketType}
            className="flex flex-col gap-3 border-t-[0.5px] border-line pt-4 first:border-t-0 first:pt-0"
          >
            <div className="flex items-center gap-2">
              <span className="font-ui text-[11px] font-medium uppercase tracking-[0.08em] text-ink-3">
                {marketType}
              </span>
              <CountBadge count={markets.length} />
            </div>
            <div className="flex flex-col gap-2">
              {markets.map((market) => (
                <MarketRow
                  key={market.id}
                  type={market.marketType}
                  name={market.name}
                  line={market.line.toFixed(1)}
                  quote={hasQuote(market.quote) ? market.quote : undefined}
                  verdict={<VerdictBadge verdict={toDisplayVerdict(market.verdict)} />}
                  muted={market.verdict === 'suspended'}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

interface AddEventsModalProps {
  readonly contestId: string;
  readonly catalogueClient: EditorCatalogueClient;
  readonly alreadyAdded: ReadonlySet<string>;
  readonly onClose: () => void;
  readonly onAdd: (events: readonly CatalogueEvent[]) => void;
}

/** Add-events modal (design): search the synced catalogue, checkbox Events with crests/meta,
 * footer tally, then "Add to slate". Markets are curated per-Event afterwards. */
function AddEventsModal({
  contestId,
  catalogueClient,
  alreadyAdded,
  onClose,
  onAdd,
}: AddEventsModalProps) {
  const { getToken } = useAdminAuth();
  const [events, setEvents] = useState<readonly CatalogueEvent[]>([]);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [search, setSearch] = useState('');

  useEffect(() => {
    const token = getToken();
    if (token === null) return;
    void catalogueClient.listEvents(token).then((result) => {
      if (result.ok) setEvents(result.events);
    });
  }, [catalogueClient, getToken]);

  const addable = events.filter((event) => !alreadyAdded.has(event.id));
  const query = search.trim().toLowerCase();
  const visible = query
    ? addable.filter((event) => event.name.toLowerCase().includes(query))
    : addable;

  function toggle(eventId: string, checked: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(eventId);
      else next.delete(eventId);
      return next;
    });
  }

  return (
    <Modal open onClose={onClose} maxWidth={640}>
      <ModalHead
        context={
          <>
            <IdChip value={contestId.slice(0, 8)} title={contestId} />
            <span>Slate</span>
          </>
        }
        title="Add events"
        subtitle="Pick events from the synced catalogue. Their props become curatable in the slate."
        onClose={onClose}
      />
      <ModalBody>
        <div className="flex flex-col gap-3.5 px-7 py-4">
          <SearchInput
            placeholder="Search events or fixtures"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          {visible.length === 0 ? (
            <p className="font-ui text-sm text-ink-3">
              {addable.length === 0
                ? 'Every synced Event is already added.'
                : 'No events match this search.'}
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              {visible.map((event) => {
                const crests = crestsForEventName(event.name);
                const competition = event.competition?.trim() || 'MLB';
                const meta = event.startTime
                  ? `${competition} · ${formatRailDate(event.startTime)}`
                  : competition;
                const checked = selected.has(event.id);
                return (
                  <div
                    key={event.id}
                    role="button"
                    tabIndex={0}
                    className="flex cursor-pointer items-center gap-3.5 rounded-tile border-[0.5px] border-line bg-surface px-3.5 py-3 hover:bg-surface-muted"
                    onClick={() => toggle(event.id, !checked)}
                    onKeyDown={(keyEvent) => {
                      if (keyEvent.key === 'Enter' || keyEvent.key === ' ') {
                        keyEvent.preventDefault();
                        toggle(event.id, !checked);
                      }
                    }}
                  >
                    <span
                      onClick={(clickEvent) => clickEvent.stopPropagation()}
                      onKeyDown={(keyEvent) => keyEvent.stopPropagation()}
                    >
                      <Checkbox
                        aria-label={event.name}
                        checked={checked}
                        onCheckedChange={(next) => toggle(event.id, next === true)}
                      />
                    </span>
                    {crests ? (
                      <CrestPair home={crests.home} away={crests.away} />
                    ) : (
                      <Crest {...lookupTeam(event.name)} />
                    )}
                    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <span className="overflow-hidden text-ellipsis whitespace-nowrap font-ui text-sm font-semibold tracking-[-0.005em] text-ink">
                        {event.name}
                      </span>
                      <span className="font-mono text-[12.5px] text-ink-4">{meta}</span>
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </ModalBody>
      <ModalFoot
        summary={
          <>
            <strong className="font-semibold text-ink">{selected.size}</strong> events selected
          </>
        }
      >
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button
          disabled={selected.size === 0}
          onClick={() => onAdd(events.filter((event) => selected.has(event.id)))}
        >
          Add to slate
        </Button>
      </ModalFoot>
    </Modal>
  );
}

interface RulesDefaults {
  readonly minPicks: number;
  readonly maxPicks: number;
  readonly voidPolicy: string;
  readonly resultCorrectionWindowMinutes: number;
}

interface RulesEditorProps {
  readonly rules: RulesDraft;
  readonly inherited: RulesDefaults | null;
  readonly onChange: (rules: RulesDraft) => void;
}

const VOID_POLICY_OPTIONS = [
  { value: 'loss', label: 'Void counts as loss' },
  { value: 'win', label: 'Void counts as win' },
];

function InheritChip({ children }: { readonly children: ReactNode }) {
  return (
    <span className="whitespace-nowrap rounded-chip border-[0.5px] border-line bg-surface-muted px-2 py-[3px] font-ui text-[11px] font-semibold uppercase tracking-[0.06em] text-ink-4">
      {children}
    </span>
  );
}

/** Rules tab (design): three rows — pick count, void policy, result-correction window — each
 * with an Inherited chip from configuration defaults and a compact override control. Pick count
 * is a single field (v1 exact-N); it writes both minPicks and maxPicks. */
function RulesEditor({ rules, inherited, onChange }: RulesEditorProps) {
  const pickDefault = inherited?.maxPicks ?? inherited?.minPicks ?? rules.maxPicks;
  const voidInherited = inherited?.voidPolicy != null;
  const correctionDefault =
    inherited?.resultCorrectionWindowMinutes ?? rules.resultCorrectionWindowMinutes;

  return (
    <Card
      title={
        <span className="flex flex-col gap-1">
          <span>Rules</span>
          <span className="font-ui text-[12.5px] font-normal tracking-normal text-ink-3">
            Inherited from configuration defaults — override per contest.
          </span>
        </span>
      }
    >
      <div className="flex flex-col">
        <div className="flex items-center gap-4 border-b-[0.5px] border-line py-4">
          <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
            <span className="font-ui text-sm font-medium leading-[1.4] text-ink">Pick count</span>
            <span className="font-ui text-[12.5px] leading-[1.45] text-ink-4">
              Number of over/under picks each player makes.
            </span>
          </div>
          <div className="flex shrink-0 items-center gap-2.5">
            <InheritChip>Inherited · {pickDefault}</InheritChip>
            <Input
              aria-label="Pick count"
              type="number"
              mono
              className="w-[130px] min-w-[130px] max-w-[130px]"
              value={rules.maxPicks}
              onChange={(event) => {
                const n = Number(event.target.value);
                onChange({ ...rules, minPicks: n, maxPicks: n });
              }}
            />
          </div>
        </div>
        <div className="flex items-center gap-4 border-b-[0.5px] border-line py-4">
          <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
            <span className="font-ui text-sm font-medium leading-[1.4] text-ink">Void policy</span>
            <span className="font-ui text-[12.5px] leading-[1.45] text-ink-4">
              How a voided pick affects the correct-count tier.
            </span>
          </div>
          <div className="flex shrink-0 items-center gap-2.5">
            {voidInherited && <InheritChip>Inherited</InheritChip>}
            <Select
              aria-label="Void policy"
              className="w-[130px] min-w-[130px] max-w-[130px] overflow-hidden"
              options={VOID_POLICY_OPTIONS}
              value={rules.voidPolicy}
              onChange={(value) => onChange({ ...rules, voidPolicy: value })}
            />
          </div>
        </div>
        <div className="flex items-center gap-4 py-4">
          <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
            <span className="font-ui text-sm font-medium leading-[1.4] text-ink">
              Result-correction window
            </span>
            <span className="font-ui text-[12.5px] leading-[1.45] text-ink-4">
              Minutes after a result posts during which corrections still apply.
            </span>
          </div>
          <div className="flex shrink-0 items-center gap-2.5">
            <InheritChip>Inherited · {correctionDefault}m</InheritChip>
            <Input
              aria-label="Result-correction window"
              type="number"
              mono
              className="w-[130px] min-w-[130px] max-w-[130px]"
              value={rules.resultCorrectionWindowMinutes}
              onChange={(event) =>
                onChange({
                  ...rules,
                  resultCorrectionWindowMinutes: Number(event.target.value),
                })
              }
            />
          </div>
        </div>
      </div>
    </Card>
  );
}

function buildSetupRailSteps(args: {
  readonly basicDone: boolean;
  readonly eventCount: number;
  readonly propCount: number;
  readonly matrix: PayoutMatrixDraft;
  readonly segmentRegistry: Record<string, string>;
  readonly maxPicks: number;
}): SetupRailStep[] {
  const slateDone = args.eventCount > 0 && args.propCount > 0;
  const segments = payoutMatrixSegments(args.segmentRegistry);
  const tiers = Array.from({ length: Math.max(args.maxPicks, 1) }, (_, i) =>
    String(args.maxPicks - i),
  );
  let unset = 0;
  for (const segment of segments) {
    for (const tier of tiers) {
      if (!args.matrix[segment]?.[tier]) unset += 1;
    }
  }
  const prizesDone = unset === 0;
  return [
    { key: 'basic-info', label: 'Basic information', done: args.basicDone },
    {
      key: 'slate',
      label: 'Slate',
      done: slateDone,
      ...(args.eventCount > 0 ? { count: args.eventCount } : {}),
    },
    {
      key: 'prizes',
      label: 'Prizes',
      done: prizesDone,
      attn: !prizesDone,
      ...(unset > 0 ? { count: unset } : {}),
    },
    { key: 'rules', label: 'Rules' },
  ];
}
